/**
 * @module SettingsMacroTargets
 * @description Daily macro nutrient targets editor.
 * Supports both auto-calculated (Mifflin-St Jeor) and manual coach mode.
 */
import React, { useState, useEffect, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useLanguage, api } from '../../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Loader2, Save, Flame, Zap, Leaf, Sliders, RotateCcw, LineChart } from 'lucide-react';
import { toast } from 'sonner';

const SettingsMacroTargets = memo(function SettingsMacroTargets() {
  const { user, updateUser } = useAuth();
  const { language, t } = useLanguage();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    daily_calories: 2000, protein_min_g: 100, protein_max_g: 180,
    carbs_target_g: 250, fats_target_g: 70, fiber_target_g: 35,
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [customByCoach, setCustomByCoach] = useState(false);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get('/users/macros');
        if (res.data) {
          setForm({
            daily_calories: res.data.daily_calories || 2000,
            protein_min_g: res.data.protein_min_g || 100,
            protein_max_g: res.data.protein_max_g || 180,
            carbs_target_g: res.data.carbs_target_g || 250,
            fats_target_g: res.data.fats_target_g || 70,
            fiber_target_g: res.data.fiber_target_g || 35,
          });
          setCustomByCoach(res.data.custom_by_coach || false);
        }
      } catch { /* use defaults */ }
    };
    load();
  }, []);

  // Iter. 406.fr.30 — CRITICAL FIX: re-sync the form whenever the AuthContext
  // user's macros change. Without this, the SettingsDeficitSelector or the
  // SettingsIMCCalculator can save a new target (e.g. TDEE 3454 − 500 = 2954
  // kcal) and the user keeps seeing the stale 2400 / 224g / 68g values from
  // mount-time, which is the bug Yuri reported. The form ALWAYS reflects the
  // latest applied macros — no more frozen 2400 fallback.
  useEffect(() => {
    if (!user) return;
    const next = {
      daily_calories: user.daily_calories,
      protein_min_g: user.protein_min_g,
      protein_max_g: user.protein_max_g,
      carbs_target_g: user.carbs_target_g,
      fats_target_g: user.fats_target_g,
      fiber_target_g: user.fiber_target_g,
    };
    // Only update fields that are defined on the user — keeps existing
    // partial edits intact if the user is mid-typing.
    setForm((prev) => {
      const merged = { ...prev };
      let changed = false;
      for (const [k, v] of Object.entries(next)) {
        if (v != null && Number(v) !== Number(prev[k])) {
          merged[k] = v;
          changed = true;
        }
      }
      return changed ? merged : prev;
    });
  }, [
    user?.daily_calories,
    user?.protein_min_g,
    user?.protein_max_g,
    user?.carbs_target_g,
    user?.fats_target_g,
    user?.fiber_target_g,
  ]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = {
        daily_calories: parseInt(form.daily_calories) || 2000,
        protein_min_g: parseInt(form.protein_min_g) || 100,
        protein_max_g: parseInt(form.protein_max_g) || 180,
        carbs_target_g: parseInt(form.carbs_target_g) || 250,
        fats_target_g: parseInt(form.fats_target_g) || 70,
        fiber_target_g: parseInt(form.fiber_target_g) || 35,
      };
      await api.put('/users/macros', data);
      updateUser({ ...user, ...data });
      toast.success(t('settings_subs.macros_saved'));
    } catch {
      toast.error('Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleResetAuto = async () => {
    setResetting(true);
    try {
      const res = await api.post('/users/macros/reset-auto');
      if (res.data.success) {
        const m = res.data.macros;
        setForm({
          daily_calories: m.daily_calories,
          protein_min_g: m.protein_min_g,
          protein_max_g: m.protein_max_g,
          carbs_target_g: m.carbs_target_g,
          fats_target_g: m.fats_target_g,
          fiber_target_g: m.fiber_target_g,
        });
        setCustomByCoach(false);
        updateUser({ ...user, active_profile: res.data.new_profile, macros_custom_set_by_coach: false, ...m });
        toast.success(language === 'fr'
          ? `Retour au calcul automatique (${res.data.new_profile}). BMR: ${res.data.bmr} kcal`
          : `Switched to auto mode (${res.data.new_profile}). BMR: ${res.data.bmr} kcal`
        );
      }
    } catch {
      toast.error(language === 'fr' ? 'Erreur: données insuffisantes pour le recalcul' : 'Error: insufficient data for recalculation');
    } finally {
      setResetting(false);
    }
  };

  return (
    <Card className="bg-card border-border">
      <CardHeader>
        <CardTitle className="font-heading text-xl uppercase tracking-tight flex items-center gap-2">
          <Flame className="w-5 h-5 text-orange-500" />
          {language === 'fr' ? 'Objectifs Macros' : 'Macro Targets'}
        </CardTitle>
        <CardDescription>
          {customByCoach ? (
            <span className="text-primary">{language === 'fr' ? '✓ Objectifs personnalises par YUMIX' : '✓ Custom targets set by YUMIX'}</span>
          ) : (
            language === 'fr' ? 'Personnalise tes objectifs quotidiens' : 'Customize your daily targets'
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {user?.active_profile === 'manual_coach' && (
          <div className="space-y-2" data-testid="manual-coach-banner">
            <div className="flex items-center gap-3 p-3 rounded-lg bg-orange-500/10 border border-orange-500/30">
              <Sliders className="w-5 h-5 text-orange-500 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-bold text-orange-500">{language === 'fr' ? 'MODE COACH (MANUEL) ACTIF' : 'COACH (MANUAL) MODE ACTIVE'}</p>
                <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Mifflin-St Jeor bypasse. Entre tes valeurs manuellement.' : 'Mifflin-St Jeor bypassed. Enter your values manually.'}</p>
              </div>
            </div>
            <Button
              type="button"
              onClick={handleResetAuto}
              disabled={resetting}
              variant="outline"
              className="w-full border-primary/40 text-primary hover:bg-primary/10 h-auto py-2.5 text-xs leading-tight whitespace-normal"
              data-testid="reset-auto-macros-btn"
            >
              {resetting ? <Loader2 className="w-4 h-4 animate-spin mr-2 shrink-0" /> : <RotateCcw className="w-4 h-4 mr-2 shrink-0" />}
              <span className="text-left">
                {language === 'fr' ? 'Désactiver le Mode Manuel' : 'Disable Manual Mode'}
                <span className="block text-[10px] opacity-70 font-normal">
                  {language === 'fr' ? 'Retour au calcul automatique IA' : 'Switch to AI Auto-Calc'}
                </span>
              </span>
            </Button>
          </div>
        )}

        {/* Calories */}
        <div className="space-y-2">
          <Label className="flex items-center gap-2 text-orange-500"><Flame className="w-4 h-4" /> {language === 'fr' ? 'Calories par jour' : 'Daily Calories'}</Label>
          <Input type="number" min="800" max="5000" step="50" value={form.daily_calories} onChange={e => setForm({...form, daily_calories: e.target.value})} className="bg-secondary border-orange-500/30 focus:border-orange-500" data-testid="macro-calories-input" />
          <div className="flex flex-wrap gap-1">
            {[1200, 1500, 1800, 2000, 2500].map(cal => (
              <Button key={cal} type="button" variant="ghost" size="sm" onClick={() => setForm({...form, daily_calories: cal})}
                className={`text-xs px-2 py-1 h-auto ${form.daily_calories == cal ? 'bg-orange-500/20 text-orange-500' : 'text-muted-foreground hover:text-orange-500'}`}>
                {cal}
              </Button>
            ))}
          </div>
        </div>

        {/* Protein Range */}
        <div className="space-y-2">
          <Label className="flex items-center gap-2 text-primary"><Zap className="w-4 h-4" /> {language === 'fr' ? 'Protéines (g/jour)' : 'Protein (g/day)'}</Label>
          <div className="grid grid-cols-2 gap-3">
            <div><span className="text-xs text-muted-foreground">Min</span>
              <Input type="number" min="20" max="400" value={form.protein_min_g} onChange={e => setForm({...form, protein_min_g: e.target.value})} className="bg-secondary border-primary/30 focus:border-primary" data-testid="macro-protein-min-input" />
            </div>
            <div><span className="text-xs text-muted-foreground">Max</span>
              <Input type="number" min="20" max="500" value={form.protein_max_g} onChange={e => setForm({...form, protein_max_g: e.target.value})} className="bg-secondary border-primary/30 focus:border-primary" data-testid="macro-protein-max-input" />
            </div>
          </div>
        </div>

        {/* Carbs & Fats */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-blue-500">{language === 'fr' ? 'Glucides (g)' : 'Carbs (g)'}</Label>
            <Input type="number" min="0" max="600" value={form.carbs_target_g} onChange={e => setForm({...form, carbs_target_g: e.target.value})} className="bg-secondary border-blue-500/30 focus:border-blue-500" data-testid="macro-carbs-input" />
          </div>
          <div className="space-y-2">
            <Label className="text-red-500">{language === 'fr' ? 'Lipides (g)' : 'Fats (g)'}</Label>
            <Input type="number" min="20" max="300" value={form.fats_target_g} onChange={e => setForm({...form, fats_target_g: e.target.value})} className="bg-secondary border-red-500/30 focus:border-red-500" data-testid="macro-fats-input" />
          </div>
        </div>

        {/* Fiber */}
        <div className="space-y-2">
          <Label className="flex items-center gap-2 text-green-500"><Leaf className="w-4 h-4" /> {language === 'fr' ? 'Fibres (g)' : 'Fiber (g)'}</Label>
          <Input type="number" min="10" max="100" value={form.fiber_target_g} onChange={e => setForm({...form, fiber_target_g: e.target.value})} className="bg-secondary border-green-500/30 focus:border-green-500 w-32" data-testid="macro-fiber-input" />
        </div>

        <Button type="button" onClick={handleSave} disabled={saving} className="w-full bg-orange-500 hover:bg-orange-600 text-white" data-testid="save-macros-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          {language === 'fr' ? 'Sauvegarder les macros' : 'Save macro targets'}
        </Button>

        {/* Iter. 406.fr.40 — Link to the new Glow chart insights page */}
        <button
          type="button"
          onClick={() => navigate('/macros/insights')}
          className="w-full flex items-center justify-between px-4 py-3 rounded-xl bg-gradient-to-r from-orange-500/10 via-purple-500/8 to-cyan-500/10 ring-1 ring-orange-500/30 hover:ring-orange-400/60 transition-all group shadow-[0_0_14px_rgba(249,115,22,0.15)]"
          data-testid="macros-open-insights-btn"
        >
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-orange-500/15 ring-1 ring-orange-500/30 flex items-center justify-center">
              <LineChart className="w-4 h-4 text-orange-300" />
            </div>
            <div className="text-left">
              <p className="text-xs font-bold uppercase tracking-widest text-orange-200">
                {language === 'fr' ? 'Voir mes graphiques' : 'View my charts'}
              </p>
              <p className="text-[10px] text-muted-foreground">
                {language === 'fr'
                  ? 'Consommation semaine · mois · année (Glow)'
                  : 'Consumption week · month · year (Glow)'}
              </p>
            </div>
          </div>
          <span className="text-orange-400 text-lg group-hover:translate-x-0.5 transition-transform">›</span>
        </button>
      </CardContent>
    </Card>
  );
});

export default SettingsMacroTargets;
